import { graphql } from 'gatsby';
import React from 'react';
import { CardsList } from 'libs/brand-system/src/templates/CardsList';
import { BlogStickyBanner } from 'libs/growth-platform-brand-system-v2/src/components/BlogStickyBanner';
import { ButtonV2 } from 'libs/growth-platform-brand-system-v2/src/components/ButtonV2';
import { container } from 'libs/growth-platform-brand-system-v2/src/styles/themes';
import { textBody } from 'libs/growth-platform-brand-system-v2/src/styles/typography';
import { HeroBlog } from 'libs/growth-platform-brand-system-v2/src/templates/HeroBlog';
import { classNames } from 'libs/growth-platform-brand-system-v2/src/utils/style';
import { ButtonTracking } from 'libs/growth-platform-brand-system-v2/src/utils/tracking/ButtonTracking';
import { useScreenSize } from 'libs/growth-platform-brand-system-v2/src/utils/utils';
import { morphism } from 'morphism';
import Layout from '../../layout';
import { HeadWrapper } from '../../layout/HeadWrapper';
import Subheader from '../../layout/subheader';
import { schemaCardsList } from '../../morphismSchema/components/schemaCardsList';
import { schemaBaseTemplate } from '../../morphismSchema/pages/schemaBaseTemplate';
import { schemaDatoButton } from '../../morphismSchema/schemaDatoButton';
import { schemaHeroBlog } from '../../morphismSchema/templates/schemaHeroBlog';

export const Head = ({ data, pageContext, location }: any) => (
  <HeadWrapper
    data={data}
    pageContext={pageContext}
    location={location}
    templateName="SubcategoryPage"
    dataName={'datoCmsSubCategoryPage'}
    largestContentfullPaintImg={null}
  />
);

export const readArticle: any = {
  fr: 'Lire l’article',
  en: 'Read the article',
  es: 'Leer el artículo',
};

export const query = graphql`
  query DatoSubCategoryPage($locale: String, $id: String) {
    datoCmsSubCategoryPage(locale: $locale, id: { eq: $id }) {
      ...SubcategoryPage
    }
    allDatoCmsBlogArticle(
      locale: $locale
      filter: { locales: { eq: $locale } }
      sort: { _allDateLocales: { value: DESC } }
    ) {
      nodes {
        slug
        metaFields {
          description
          title
        }
        subCategory {
          id
        }
      }
    }
    datoCmsBlogHome(locale: $locale) {
      slug
      trackingPageName {
        title
      }
      header {
        ...CustomMenu
      }
    }
  }
`;

const SubcategoryPage = ({ data, pageContext }: any) => {
  const doc = data.datoCmsSubCategoryPage;
  const homepage = data.datoCmsBlogHome;
  const articles =
    data.allDatoCmsBlogArticle?.nodes?.filter((art: any) => {
      return art?.subCategory?.id === pageContext.id;
    }) ?? [];

  const { stickyBanner, hero } = morphism(schemaBaseTemplate, {
    ...doc,
    pageContext,
  });
  const screenSize = useScreenSize();
  const isDesktop = !['xs', 'sm', 'md'].includes(screenSize);

  const ctaStickyBanner = morphism(schemaDatoButton, {
    ...stickyBanner.cta?.[0],
    locale: pageContext.locale,
  });

  const ctaStickyBannerResponsive = morphism(schemaDatoButton, {
    ...stickyBanner?.cta?.[0],
    isTitleResponsive: true,
    screenSize,
    locale: pageContext?.locale,
  });

  return (
    <Layout
      pageContext={pageContext}
      data={doc}
      customHeader={homepage?.header}
      templateName="SubcategoryPage"
    >
      <div className={classNames(container)}>
        <Subheader
          doc={doc}
          hero={hero}
          slugBase={homepage.slug}
          pageContext={pageContext}
        />
      </div>

      <HeroBlog
        {...(morphism(schemaHeroBlog, {
          ...hero,
          locale: pageContext.locale,
        }) as any)}
        isCentered={true}
        locale={pageContext?.locale}
        className={classNames('mx-24 md:mx-auto')}
      />
      {stickyBanner && !isDesktop && (
        <BlogStickyBanner
          key={stickyBanner.id}
          title={stickyBanner?.title}
          image={stickyBanner.image}
          className={classNames('max-w-[320px] mx-auto mt-[64px]')}
          titleClassName={textBody}
          cta={
            <ButtonTracking
              component={ButtonV2}
              {...ctaStickyBannerResponsive}
              labelTracking={ctaStickyBannerResponsive.title}
            />
          }
        />
      )}
      <div className="lg:grid lg:grid-cols-12 lg:gap-24 container py-40 lg:py-104">
        <div className="lg:col-span-8">
          <CardsList
            {...morphism(schemaCardsList, {
              cards: articles,
              screenSize,
              locale: pageContext?.locale,
            })}
            layout="two_columns"
            cardsContainerClass="flex flex-wrap gap-24 justify-between"
          />
        </div>
        {stickyBanner && isDesktop && (
          <div className="lg:col-span-4 lg:col-start-9">
            <BlogStickyBanner
              key={stickyBanner.id}
              title={stickyBanner.title}
              image={stickyBanner.image}
              cta={
                <ButtonTracking
                  component={ButtonV2}
                  {...ctaStickyBanner}
                  labelTracking={ctaStickyBanner.title}
                />
              }
            />
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SubcategoryPage;
